import { isJobFileUrlVersionV2 } from './load-master-status-navigation.util';

/**
 * Single source of truth for load-master / job-file status presentation.
 *
 * Raw statuses reach the UI in several shapes for the same state, because different
 * services write them: ms-control-tower writes 'TERMINATE' while the job-file service writes
 * 'TERMINATED', and the job-file status uses 'INPROGRESS' where load-master uses
 * 'IN_PROGRESS'. Cross-tenancy jobs (a job sent from one org and accepted by another)
 * surface both shapes side by side, so labels and colors must be derived from a
 * normalized code instead of raw string comparisons.
 */

/** Canonical status code used for labels, css classes and comparisons. */
export function normalizeLoadMasterStatus(status?: string | null): string {
  const normalized = (status || '').toUpperCase().trim().replace(/[\s-]+/g, '_');
  switch (normalized) {
    case 'TERMINATE':
    case 'TERMINATED':
      return 'TERMINATED';
    case 'INPROGRESS':
    case 'JOB_IN_PROGRESS':
    case 'IN_PROGRESS':
      return 'IN_PROGRESS';
    case 'SCHEDULED_NOTIFIED':
    case 'SCHEDULE_NOTIFIED':
      return 'SCHEDULE_NOTIFIED';
    case 'PARTIAL_COMPLETED':
    case 'PARTIALLY_COMPLETED':
      return 'PARTIALLY_COMPLETED';
    case 'ONHOLD':
    case 'ON_HOLD':
      return 'ON_HOLD';
    default:
      return normalized;
  }
}

const LOAD_MASTER_STATUS_LABELS: Record<string, string> = {
  TERMINATED: 'Terminated',
  COMPLETED: 'Completed',
  IN_PROGRESS: 'In-Progress',
  // Dynamic Control Tower states
  PARTIALLY_COMPLETED: 'Partially Completed',
  ON_HOLD: 'On Hold',
  RECONCILIATION_PENDING: 'Reconciliation Pending',
  SCHEDULE_NOTIFIED: 'Scheduled-Notified',
  SCHEDULED: 'Scheduled',
  PLANNING: 'Planning',
  PENDING: 'Pending',
  SR_PENDING: 'SR Pending',
  SR_ACCEPTED: 'SR Accepted',
  SR_REJECTED: 'SR Rejected',
  SR_EXPIRED: 'SR Expired',
  RFQ_ACTIVE: 'RFQ Active',
  RFQ_NEGOTIATION: 'RFQ Negotiation',
  RFQ_EXPIRED: 'RFQ Expired',
  RFQ_AWARDED: 'RFQ Awarded',
};

/**
 * Human label for a raw status, identical for every tenancy in the job chain.
 *
 * `urlVersion` is the job-file version the load belongs to. On v2 a load reaches SCHEDULED by
 * having a contract or quotation assigned to it on the load-assignment page, so it reads as
 * 'Contracted' there; v1 keeps 'Scheduled', where the status still means the schedule itself.
 */
export function getLoadMasterStatusLabel(status?: string | null, urlVersion?: string | null): string {
  const normalized = normalizeLoadMasterStatus(status);
  if (!normalized) {
    return '';
  }
  if (normalized === 'SCHEDULED' && isJobFileUrlVersionV2(urlVersion)) {
    return 'Contracted';
  }
  const label = LOAD_MASTER_STATUS_LABELS[normalized];
  if (label) {
    return label;
  }
  return normalized
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Css class for a raw status, e.g. 'job-state-schedule-notified'. Derived from the
 * normalized code (never the label) so it can never contain a space — a space in an
 * ngClass string splits into two bogus classes and the chip renders unstyled.
 */
export function getLoadMasterStatusClass(status?: string | null, prefix = 'job-state-'): string {
  const normalized = normalizeLoadMasterStatus(status);
  if (!normalized) {
    return `${prefix}unknown`;
  }
  return `${prefix}${normalized.toLowerCase().replace(/_/g, '-')}`;
}

export function isLoadMasterStatusTerminated(status?: string | null): boolean {
  return normalizeLoadMasterStatus(status) === 'TERMINATED';
}

/** Job Summary stage names (load-master.jobSummaryStatus), same words as the dashboard tabs. */
const JOB_SUMMARY_STATUS_LABELS: Record<string, string> = {
  PLANNING: 'Planning',
  LOAD_ASSIGNMENT: 'Load Assignment',
  CONTRACTED: 'Contracted',
  PENDING_ACCEPTANCE: 'Pending Acceptance',
  ACCEPTED: 'Accepted',
  NOTIFIED: 'Notified',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  TERMINATED: 'Terminated'
};

/**
 * Job Master label: the load's Job Summary stage when it is stored, otherwise the status label
 * (loads without a stored stage, or a status no stage covers). Mirrors the ms-report logic.
 */
export function getJobSummaryStatusLabel(jobSummaryStatus?: string | null, status?: string | null, urlVersion?: string | null): string {
  return JOB_SUMMARY_STATUS_LABELS[(jobSummaryStatus || '').toUpperCase()] || getLoadMasterStatusLabel(status, urlVersion);
}

/** Chip colour per Job Summary stage, reusing the status colours the stage replaces. */
const JOB_SUMMARY_STATUS_CLASS_SUFFIX: Record<string, string> = {
  PLANNING: 'planning',
  LOAD_ASSIGNMENT: 'load-assignment',
  CONTRACTED: 'scheduled',
  PENDING_ACCEPTANCE: 'pending',
  ACCEPTED: 'sr-accepted',
  NOTIFIED: 'schedule-notified',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  TERMINATED: 'terminated'
};

/**
 * Job Master chip class: follows the stored Job Summary stage, so a stage has one colour
 * whatever raw status got the load there (an SR keeps SCHEDULED, an RFQ is PENDING - both Pending
 * Acceptance). Loads without a stage keep the raw status class.
 */
export function getJobSummaryStatusClass(jobSummaryStatus?: string | null, status?: string | null, prefix = 'job-state-'): string {
  const suffix = JOB_SUMMARY_STATUS_CLASS_SUFFIX[(jobSummaryStatus || '').toUpperCase()];
  return suffix ? `${prefix}${suffix}` : getLoadMasterStatusClass(status, prefix);
}
